<template>
  <div class="task-batch-status" :title="displayStatus">
    <div class="task-batch-status__row">
      <span class="task-batch-status__label">Status:</span>
      <span class="task-batch-status__state">{{ displayStatus }}</span>
    </div>
    <div class="task-batch-status__process">
      <div class="task-batch-status__id">{{ displayProcessId }}</div>
      <button
        v-if="processId"
        type="button"
        class="task-batch-status__copy"
        :title="copied ? 'Copied' : 'Copy process ID'"
        :aria-label="copied ? 'Process ID copied' : 'Copy process ID'"
        @click.stop="copyProcessId"
      >
        <SVGIcon :iconName="'copy'" :width="14" :height="14" />
      </button>
    </div>
    <img v-if="showStatusIcon" :src="statusIcon" class="task-batch-status__icon" alt="status icon">
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import SVGIcon from '@/components/SVGIcon.vue';
import checkIcon from '@/assets/img/svg/check-button.svg';
import failedIcon from '@/assets/img/svg/failed.svg';
import questionIcon from '@/assets/img/svg/question.svg';
import personIcon from '@/assets/img/svg/person.svg';
import terminatedIcon from '@/assets/img/terminated.png';
import runningIcon from '@/assets/img/animated/running.gif';
import hourglassIcon from '@/assets/img/animated/hourglass.gif';

const props = defineProps({
  process_info: {
    type: Object,
    default: () => ({
      id: '',
      status: '',
    }),
  },
});

const normalizedState = computed(() => (props.process_info?.status || '').trim().toLowerCase());
const processId = computed(() => String(props.process_info?.id ?? '').trim());
const copied = ref(false);
const displayStatus = computed(() => {
  const manageType = String(props.process_info?.manage_type || '').trim().toLowerCase();
  if (manageType === 'user') {
    return 'User Managed';
  }

  return props.process_info?.status || 'Unknown';
});
const normalizedDisplayStatus = computed(() => String(displayStatus.value || '').trim().toLowerCase());
const showStatusIcon = computed(() => normalizedState.value !== '');
const displayProcessId = computed(() => {
  return processId.value || 'No Server Process';
});

async function copyProcessId() {
  if (!processId.value) return;

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(processId.value);
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = processId.value;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const didCopy = document.execCommand('copy');
      textarea.remove();
      if (!didCopy) throw new Error('Clipboard copy failed');
    }

    copied.value = true;
    window.setTimeout(() => { copied.value = false; }, 1500);
  } catch (error) {
    console.error('Failed to copy process ID:', error);
  }
}

const statusIcon = computed(() => {
  if (!showStatusIcon.value) {
    return null;
  }

  if (normalizedDisplayStatus.value.includes('user')) {
    return personIcon;
  }

  if (!normalizedState.value || normalizedState.value === 'unknown') {
    return questionIcon;
  }

  if (normalizedState.value === 'terminated' || normalizedState.value.includes('terminated')) {
    return terminatedIcon;
  }

  if (normalizedState.value === 'succeeded' || normalizedState.value === 'complete') {
    return checkIcon;
  }

  if (normalizedState.value === 'failed' || normalizedState.value === 'stopped' || normalizedState.value === 'error') {
    return failedIcon;
  }

  if (normalizedState.value === 'running' || normalizedState.value === 'copying') {
    return runningIcon;
  }

  return hourglassIcon;
});
</script>

<style scoped lang="scss">
.task-batch-status {
  padding: 0 8px;
  height: fit-content;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  column-gap: 8px;
  row-gap: 4px;
  width: 100%;
}

.task-batch-status__row {
  display: flex;
  align-items: center;
  gap: 6px;
  grid-column: 1;
  grid-row: 1;
}

.task-batch-status__label {
  font-size: 11px;
  color: var(--body-text-color-secondary, #666);
  font-weight: 600;
}

.task-batch-status__state {
  font-size: 11px;
  color: var(--body-text-color, #222);
  font-weight: 600;
}

.task-batch-status__icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
  grid-column: 2;
  grid-row: 1;
  align-self: center;
  justify-self: end;
}

.task-batch-status__process {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  grid-column: 1 / 3;
  grid-row: 2;
}

.task-batch-status__id {
  font-size: 9px;
  line-height: 1;
  color: var(--body-text-color-secondary, #888);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.task-batch-status__copy {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--body-text-color-secondary, #666);
  cursor: pointer;
  flex: 0 0 auto;
}

.task-batch-status__copy:hover {
  color: var(--body-text-color, #222);
}
</style>
